import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AddressHistoryResponse,
  AssetResponse,
  BorrowerResponse,
  CreateAddressHistoryRequest,
  CreateAssetRequest,
  CreateBorrowerRequest,
  CreateIncomeRequest,
  CreateLoanRequest,
  CreditScoreResponse,
  ENVIRONMENT,
  IncomeResponse,
  OriginationLoanType,
  PurchaseLoanResponse,
  RefinanceGoal,
  UpdateBorrowerRequest,
  UpdateLoanRequest,
} from '@ld.consumer-portal/models';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { MlaAuthService } from './mla-auth.service';

export type {
  AddressHistoryResponse,
  AssetAccountType,
  AssetResponse,
  BorrowerDeclarationsRequest,
  BorrowerDeclarationsResponse,
  BorrowerHmdaRequest,
  BorrowerHmdaResponse,
  BorrowerResponse,
  CreateAddressHistoryRequest,
  CreateAssetRequest,
  CreateBorrowerRequest,
  CreateIncomeRequest,
  CreateLoanRequest,
  CreditBureau,
  CreditPullStatus,
  CreditReportResponse,
  CreditScoreResponse,
  CurrentResidenceRequest,
  CurrentResidenceResponse,
  ExplanationType,
  GovDeclarationsPropertyType,
  GovDeclarationsTitleType,
  IncomeResponse,
  IncomeSourceType,
  ManufacturedOwnership,
  ManufacturedPropertyType,
  MaritalStatus,
  MilitaryBranch,
  MilitaryStatusType,
  NameSuffix,
  NewUrlaAssetAccountType,
  OtherIncomeType,
  PropertyType,
  PropertyUsage,
  PurchaseStatus,
  RefinanceGoal,
  RelationshipStatus,
  ResidenceType,
  SubjectPropertyRequest,
  SubjectPropertyResponse,
  TaxFrequency,
  UpdateBorrowerRequest,
  UpdateLoanRequest,
} from '@ld.consumer-portal/models';

export interface LoanResponse extends PurchaseLoanResponse {
  refinanceGoal?: RefinanceGoal;
  refinanceCashOut?: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  errors?: Array<{ [key: string]: string }>;
}

@Injectable({
  providedIn: 'root',
})
export class MlaApplicationService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(MlaAuthService);

  private get mlaApiUrl(): string {
    return this.env.appSettings.mlaApiUrl || '';
  }

  createLoan(
    loanType: OriginationLoanType,
    options?: { loanOfficerId?: string; refinanceGoal?: RefinanceGoal; refinanceCashOut?: number }
  ): Observable<LoanResponse | null> {
    const url = `${this.mlaApiUrl}api/v1/loan`;
    const payload: CreateLoanRequest = {
      loanType,
      loanOfficerId: options?.loanOfficerId,
      refinanceGoal: options?.refinanceGoal,
      refinanceCashOut: options?.refinanceCashOut,
    };

    return this.http
      .post<ApiResponse<LoanResponse>>(url, payload)
      .pipe(map((response) => (response?.success ? response.data : null)));
  }

  createPurchaseLoan(loanOfficerId?: string): Observable<LoanResponse | null> {
    return this.createLoan('Purchase', { loanOfficerId });
  }

  createRefinanceLoan(options?: {
    loanOfficerId?: string;
    refinanceGoal?: RefinanceGoal;
    refinanceCashOut?: number;
  }): Observable<LoanResponse | null> {
    return this.createLoan('Refinance', options);
  }

  updateLoan(loanId: string, updates: UpdateLoanRequest): Observable<boolean> {
    const url = `${this.mlaApiUrl}api/v1/loan/${loanId}`;

    return this.http.patch<ApiResponse<unknown>>(url, updates).pipe(map((response) => response?.success || false));
  }

  submitApplication(loanId: string): Observable<boolean> {
    return this.updateLoan(loanId, { hasSubmit: true });
  }

  createBorrower(request: CreateBorrowerRequest): Observable<BorrowerResponse | null> {
    const url = `${this.mlaApiUrl}api/v1/borrower`;

    return this.http
      .post<ApiResponse<BorrowerResponse>>(url, request)
      .pipe(map((response) => (response?.success ? response.data : null)));
  }

  updateBorrower(borrowerId: string, updates: UpdateBorrowerRequest): Observable<boolean> {
    const url = `${this.mlaApiUrl}api/v1/borrower/${borrowerId}`;

    return this.http.patch<ApiResponse<unknown>>(url, updates).pipe(map((response) => response?.success || false));
  }

  getBorrowers(loanId: string): Observable<BorrowerResponse[]> {
    const url = `${this.mlaApiUrl}api/v1/borrower?loanId=${loanId}`;

    return this.http.get<ApiResponse<BorrowerResponse[]>>(url).pipe(map((response) => response?.data || []));
  }

  getIncomes(borrowerId: string): Observable<IncomeResponse[]> {
    const url = `${this.mlaApiUrl}api/v1/income?borrowerId=${borrowerId}`;

    return this.http.get<ApiResponse<IncomeResponse[]>>(url).pipe(map((response) => response?.data || []));
  }

  createIncome(request: CreateIncomeRequest): Observable<IncomeResponse | null> {
    const url = `${this.mlaApiUrl}api/v1/income`;

    return this.http
      .post<ApiResponse<IncomeResponse>>(url, request)
      .pipe(map((response) => (response?.success ? response.data : null)));
  }

  updateIncome(incomeId: string, updates: Partial<CreateIncomeRequest>): Observable<boolean> {
    const url = `${this.mlaApiUrl}api/v1/income/${incomeId}`;

    return this.http.patch<ApiResponse<unknown>>(url, updates).pipe(map((response) => response?.success || false));
  }

  deleteIncome(incomeId: string): Observable<boolean> {
    const url = `${this.mlaApiUrl}api/v1/income/${incomeId}`;

    return this.http.delete<ApiResponse<unknown>>(url).pipe(map((response) => response?.success || false));
  }

  getAssets(borrowerId: string): Observable<AssetResponse[]> {
    const url = `${this.mlaApiUrl}api/v1/asset?borrowerId=${borrowerId}`;

    return this.http.get<ApiResponse<AssetResponse[]>>(url).pipe(map((response) => response?.data || []));
  }

  createAsset(request: CreateAssetRequest): Observable<AssetResponse | null> {
    const url = `${this.mlaApiUrl}api/v1/asset`;

    return this.http
      .post<ApiResponse<AssetResponse>>(url, request)
      .pipe(map((response) => (response?.success ? response.data : null)));
  }

  updateAsset(assetId: string, updates: Partial<CreateAssetRequest>): Observable<boolean> {
    const url = `${this.mlaApiUrl}api/v1/asset/${assetId}`;

    return this.http.patch<ApiResponse<unknown>>(url, updates).pipe(map((response) => response?.success || false));
  }

  deleteAsset(assetId: string): Observable<boolean> {
    const url = `${this.mlaApiUrl}api/v1/asset/${assetId}`;

    return this.http.delete<ApiResponse<unknown>>(url).pipe(map((response) => response?.success || false));
  }

  getAddressHistory(borrowerId: string): Observable<AddressHistoryResponse[]> {
    const url = `${this.mlaApiUrl}api/v1/address-history?borrowerId=${borrowerId}`;

    return this.http.get<ApiResponse<AddressHistoryResponse[]>>(url).pipe(map((response) => response?.data || []));
  }

  createAddressHistory(request: CreateAddressHistoryRequest): Observable<AddressHistoryResponse | null> {
    const url = `${this.mlaApiUrl}api/v1/address-history`;

    return this.http
      .post<ApiResponse<AddressHistoryResponse>>(url, request)
      .pipe(map((response) => (response?.success ? response.data : null)));
  }

  updateAddressHistory(addressHistoryId: string, updates: Partial<CreateAddressHistoryRequest>): Observable<boolean> {
    const url = `${this.mlaApiUrl}api/v1/address-history/${addressHistoryId}`;

    return this.http.patch<ApiResponse<unknown>>(url, updates).pipe(map((response) => response?.success || false));
  }

  deleteAddressHistory(addressHistoryId: string): Observable<boolean> {
    const url = `${this.mlaApiUrl}api/v1/address-history/${addressHistoryId}`;

    return this.http.delete<ApiResponse<unknown>>(url).pipe(map((response) => response?.success || false));
  }

  getCreditScores(borrowerId: string): Observable<CreditScoreResponse[]> {
    const url = `${this.mlaApiUrl}api/v1/borrower/${borrowerId}/credit-score`;

    return this.http.get<ApiResponse<CreditScoreResponse[]>>(url).pipe(map((response) => response?.data || []));
  }

  getLoan(loanId: string): Observable<LoanResponse | null> {
    const url = `${this.mlaApiUrl}api/v1/loan/${loanId}`;

    return this.http
      .get<ApiResponse<LoanResponse>>(url)
      .pipe(map((response) => (response?.success ? response.data : null)));
  }

  getBorrower(borrowerId: string): Observable<BorrowerResponse | null> {
    const url = `${this.mlaApiUrl}api/v1/borrower/${borrowerId}`;

    return this.http
      .get<ApiResponse<BorrowerResponse>>(url)
      .pipe(map((response) => (response?.success ? response.data : null)));
  }

  async createLoanAsync(
    loanType: OriginationLoanType,
    options?: { loanOfficerId?: string; refinanceGoal?: RefinanceGoal; refinanceCashOut?: number }
  ): Promise<LoanResponse | null> {
    return firstValueFrom(this.createLoan(loanType, options));
  }

  async createPurchaseLoanAsync(loanOfficerId?: string): Promise<LoanResponse | null> {
    return firstValueFrom(this.createPurchaseLoan(loanOfficerId));
  }

  async createRefinanceLoanAsync(options?: {
    loanOfficerId?: string;
    refinanceGoal?: RefinanceGoal;
    refinanceCashOut?: number;
  }): Promise<LoanResponse | null> {
    return firstValueFrom(this.createRefinanceLoan(options));
  }

  async createBorrowerAsync(request: CreateBorrowerRequest): Promise<BorrowerResponse | null> {
    return firstValueFrom(this.createBorrower(request));
  }

  async updateBorrowerAsync(borrowerId: string, updates: UpdateBorrowerRequest): Promise<boolean> {
    return firstValueFrom(this.updateBorrower(borrowerId, updates));
  }

  async updateLoanAsync(loanId: string, updates: UpdateLoanRequest): Promise<boolean> {
    return firstValueFrom(this.updateLoan(loanId, updates));
  }

  async submitApplicationAsync(loanId: string): Promise<boolean> {
    return firstValueFrom(this.submitApplication(loanId));
  }

  async createIncomeAsync(request: CreateIncomeRequest): Promise<IncomeResponse | null> {
    return firstValueFrom(this.createIncome(request));
  }

  async createAssetAsync(request: CreateAssetRequest): Promise<AssetResponse | null> {
    return firstValueFrom(this.createAsset(request));
  }

  async getCreditScoresAsync(borrowerId: string): Promise<CreditScoreResponse[]> {
    return firstValueFrom(this.getCreditScores(borrowerId));
  }
}
